import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowDownRight, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import { ATHENA } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSuccess("");
    setError("");
    if (!form.name || !form.email || !form.company || !form.subject || !form.message) {
      setError("Please fill in all fields before submitting.");
      return;
    }
    if (form.message.length < 5) {
      setError("Your message is a little too short.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      setSuccess(data.message || "Thank you. We'll be in touch shortly.");
      toast.success("Inquiry sent. We'll reply within one business day.");
      setForm({ name: "", email: "", company: "", subject: "", message: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email contact@athenanutrascience.com directly.";
      setError(typeof msg === "string" ? msg : "Submission failed.");
      toast.error("Could not submit your inquiry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid={ATHENA.contactSection}
      className="relative bg-[#0A1713] text-[#F8F7F4] py-12 md:py-16"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        {/* Left */}
        <div className="lg:col-span-5">
          <span className="label-mono">// START A CONVERSATION</span>
          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.8 }}
            className="font-display text-4xl sm:text-5xl md:text-6xl mt-6 leading-[1.02] tracking-tight"
          >
            Let&apos;s build the
            <br />
            next molecule.
          </motion.h2>
          <p className="mt-8 text-[#A2B3AB] leading-relaxed max-w-md">
            Whether you&apos;re a brand, a clinical group, or a research lab — tell
            us what you&apos;re working on. We respond personally to every inquiry
            within one business day.
          </p>

          <div className="mt-12 space-y-6 text-sm">
            <div className="flex items-start gap-4">
              <Mail size={18} strokeWidth={1.4} className="text-[#B89B64] mt-1" />
              <div>
                <div className="label-mono">EMAIL</div>
                <div className="mt-1">connect@athenanutrascience.com</div>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <MapPin size={18} strokeWidth={1.4} className="text-[#B89B64] mt-1" />
              <div>
                <div className="label-mono">HEADQUARTERS</div>
                <div className="mt-1 leading-relaxed">
                  Athena Nutrascience Pvt. Ltd.
                  <br />
                  Mumbai 400 063, India
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <motion.form
          data-testid={ATHENA.contactForm}
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="lg:col-span-7 lg:pl-10 lg:border-l lg:border-[#1A382A]"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
            <div>
              <label className="label-mono">Name *</label>
              <input
                data-testid={ATHENA.contactName}
                className="athena-input athena-input-dark"
                name="name"
                value={form.name}
                onChange={onChange}
                placeholder="Dr. Ada Lovelace"
                autoComplete="name"
              />
            </div>
            <div>
              <label className="label-mono">Email *</label>
              <input
                data-testid={ATHENA.contactEmail}
                className="athena-input athena-input-dark"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                placeholder="you@company.com"
                autoComplete="email"
              />
            </div>
            <div>
              <label className="label-mono">Company / Institution</label>
              <input
                data-testid={ATHENA.contactCompany}
                className="athena-input athena-input-dark"
                name="company"
                value={form.company}
                onChange={onChange}
                placeholder="Acme Bio Labs"
                autoComplete="organization"
              />
            </div>
            <div>
              <label className="label-mono">Subject *</label>
              <input
                data-testid={ATHENA.contactSubject}
                className="athena-input athena-input-dark"
                name="subject"
                value={form.subject}
                onChange={onChange}
                placeholder="Partnership inquiry"
              />
            </div>
          </div>

          <div className="mt-8">
            <label className="label-mono">Message *</label>
            <textarea
              data-testid={ATHENA.contactMessage}
              className="athena-input athena-input-dark resize-none"
              rows={4}
              name="message"
              value={form.message}
              onChange={onChange}
              placeholder="Tell us about your project, timeline, and what you're hoping to explore."
            />
          </div>

          {error && (
            <div
              data-testid={ATHENA.contactError}
              className="mt-6 text-sm text-[#f6c5b5] border-l-2 border-[#B89B64] pl-3"
            >
              {error}
            </div>
          )}
          {success && (
            <div
              data-testid={ATHENA.contactSuccess}
              className="mt-6 text-sm text-[#B89B64] border-l-2 border-[#B89B64] pl-3"
            >
              {success}
            </div>
          )}

          <button
            data-testid={ATHENA.contactSubmit}
            type="submit"
            disabled={loading}
            className="btn-primary btn-on-dark mt-10 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Sending…" : "Send inquiry"}
            <ArrowDownRight size={16} strokeWidth={1.5} />
          </button>

          <p className="mt-6 text-xs text-[#A2B3AB] max-w-md leading-relaxed">
            By submitting, you agree to be contacted by the Athena team.
            <br />
            We never share your information with third parties.
          </p>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
